import clsx from 'clsx'
import { Component } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { FormattedMessage, injectIntl, type WrappedComponentProps } from 'react-intl'

import { packageViewMoreDetailsClick } from 'actions/AnalyticsActions'
import { authLogout } from 'actions/AuthActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { authModalOpen } from 'actions/UiActions'
import Pane from 'components/Common/Pane'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import WalledContent from 'components/Common/WalledContent'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import { rem } from 'polished'
import FlashTourPackagePricing from './FlashTourPackagePricing'
import FlashTourDurationLabel from './FlashTourDurationLabel'

import PaneBody from 'components/Common/Pane/PaneBody'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const PackageInfoContent = styled.div`
  padding: ${rem(20)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.largeDesktop} {
    border: 0;
  }

  > * + * {
    margin-top: ${rem(20)};
  }
`

const PackageOptionWrapper = styled(PaneBody)`
  ${mediaQueryUp.tablet} {
    display: -ms-grid;
    display: grid;
    -ms-grid-columns: 1fr 1fr;
    grid-template-rows: min-content 1fr;
    grid-template-columns: repeat(2,1fr);
  }

  ${mediaQueryUp.largeDesktop} {
    -ms-grid-columns: ${rem(320)} ${rem(438)} ${rem(382)};
    grid-template-columns: ${rem(320)} ${rem(438)} ${rem(382)};
  }
`

const PackageDetailsWrapper = styled.div`
  display: flex;
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    -ms-grid-column-span: 2;
    -ms-grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.largeDesktop} {
    flex-direction: column;
    -ms-grid-column: 1;
    -ms-grid-column-span: 1;
    grid-column: span 1;
    grid-row: 2;
  }
`

const InclusionHighlights = styled(PackageInfoContent)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    -ms-grid-row: 3;
  }

  ${mediaQueryUp.largeDesktop} {
    padding-inline: 0;
    -ms-grid-column: 2;
    -ms-grid-row: 2;
    grid-column: 2;
    grid-row: 2;
  }
`

const PackageInfo = styled(PackageInfoContent)`
  ${mediaQueryUp.tablet} {
    order: 1;
  }

  ${mediaQueryUp.tablet} {
    -ms-grid-row: 3;
    -ms-grid-column: 2;
    display: flex;
    flex-direction: column;
  }

  ${mediaQueryUp.largeDesktop} {
    -ms-grid-column: 3;
    -ms-grid-row: 2;
    -ms-grid-column-span: 2;
    grid-row: span 2;
  }
`

const PackageDetails = styled.div`
  ${mediaQueryDown.desktop} {
    flex: 1;
  }

  ${mediaQueryDown.mobile} {
    flex: 1.5;
  }
`

const StyledPaneDetailsSection = styled(PaneDetailsSection)`
  flex-basis: 100%;
`

const PackageDescription = styled.div`
  overflow-y: hidden;
  position: relative;
  flex-grow: 1;
  max-height: ${rem(213)};

  &::after {
    content: "";
    display: block;
    height: ${rem(48)};
    inset-inline: 0;
    position: absolute;
    bottom:0;
    top: ${rem(165)};
    z-index: ${zIndex.min};
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 85%);
  }
`

const AllRoomDetails = styled(VerticalSpacer)`
  padding-block: ${rem(30)} ${rem(25)};
  padding-inline: ${rem(35)};

  ul {
    li {
      margin-top: 0;
      font-size: ${rem(14)};

      & + li {
        margin-top: ${rem(2)};
      }
    }
  }
`

const ToggleWrapper = styled(BodyText)`
  margin-top: ${rem(16)};
`

interface Props extends WrappedComponentProps {
  authLogout: Utils.ActionDispatcher<typeof authLogout>
  authModalOpen: Utils.ActionDispatcher<typeof authModalOpen>
  availableForPurchase: boolean
  className?: string
  memberId: string
  offer: App.TourOffer
  packageOption: App.TourPackage
  pushWithRegion: Utils.ActionDispatcher<typeof pushWithRegion>
  search: string
  currency: string
  defaultDepositAmountPercentage: number
  useNightDuration?: boolean
}

interface State {
  carouselOpened: boolean
  detailsExpanded: boolean
}

class FlashTourPackageOption extends Component<React.PropsWithChildren<Props>, State> {
  checkoutSubmit = false
  state = {
    carouselOpened: false,
    detailsExpanded: false,
  }

  loginBeforeCheckout() {
    this.props.authLogout()
    this.props.authModalOpen(this.props.intl.formatMessage({
      id: 'blcJ4eZ0Mb5XF70jx42X',
      defaultMessage: 'Please log in to continue',
    }))
  }

  toggleCarousel = () => {
    this.setState({
      carouselOpened: !this.state.carouselOpened,
    })
  }

  toggleDetails = () => {
    this.setState({
      detailsExpanded: !this.state.detailsExpanded,
    })
  }

  render() {
    const {
      memberId,
      availableForPurchase,
      className,
      offer,
      packageOption,
      currency,
      defaultDepositAmountPercentage,
      useNightDuration,
      intl,
    } = this.props

    const {
      description,
      durationLabel,
      inclusions,
      name,
      shouldDisplayValue,
    } = packageOption

    const {
      type,
      offerFlightsEnabled,
    } = offer

    const isFlightsEnabledWithTours = type === OFFER_TYPE_TOUR && offerFlightsEnabled
    const nightDurationLabel = intl.formatMessage(
      {
        id: 'p-6Mdf7Lhf1ZB9cjo04W',
        defaultMessage: '{nights, plural, one {# night} other {# nights}}',
      },
      { nights: offer.minDuration! - 1 },
    )
    const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, undefined, packageOption.price)
    const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

    const inclusionsDesc = inclusions?.map(inclusion => inclusion.description).join('\n') ?? ''

    const logInToSeePriceLabel = intl.formatMessage({
      id: 'D9nTeMgdoIHgCRFWSzA3',
      defaultMessage: 'Log in to see price',
    })

    const hideInclusionsLabel = <FormattedMessage
      id="Heos2jIy36RqEBlHdApU"
      defaultMessage="Hide all inclusions & details"
      
    />
    const viewInclusionsLabel = <FormattedMessage
      id="bvuBdNKP8AM0xQUrGzfz"
      defaultMessage="View all inclusions & details"
      
    />

    return <Pane
      shadow="bottom-small"
      data-testid={clsx('package-option')}
      className={className}
    >
      <PackageOptionWrapper>
        <PackageDetailsWrapper>
          <PackageDetails>
            <FlashTourDurationLabel durationLabel={useNightDuration ? nightDurationLabel : durationLabel} />
            <Heading variant="heading5">{name}</Heading>
          </PackageDetails>
        </PackageDetailsWrapper>
        <PackageInfo>
          <WalledContent
            buttonText={logInToSeePriceLabel}
            enforced={offer.walledGarden}
          >
            {isDepositEnabled && <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} showDepositAmountPercentage />}
            <FlashTourPackagePricing
              memberId={memberId}
              durationLabel={useNightDuration ? nightDurationLabel : durationLabel}
              packageOption={packageOption}
              offer={offer}
              availableForPurchase={availableForPurchase}
              shouldDisplayValue={shouldDisplayValue}
            />
            {isFlightsEnabledWithTours && <CaptionTextBlock className={className} variant="medium" align="center" startIcon={<LinePlaneFlyIcon />}>
              <FormattedMessage
                id="efh0cO7VsyUK2o7wulXW"
                defaultMessage="Tour + flights package available"
                
              />
            </CaptionTextBlock>}
          </WalledContent>
        </PackageInfo>
        {(inclusionsDesc || description) && <InclusionHighlights>
          <BodyText variant="large" weight="bold">
            <FormattedMessage
              id="_E7X5vl_95DyJ5hbfGUm"
              defaultMessage="Inclusion highlights:"
              
            />
          </BodyText>
          <PackageDescription>
            <MarkdownRender fontSize="medium" type="compact" content={inclusionsDesc ?? description} />
          </PackageDescription>
          <ToggleWrapper variant="medium" onClick={packageViewMoreDetailsClick}>
            <SmallToggleButton
              on={this.state.detailsExpanded}
              onClick={this.toggleDetails}
            >
              {this.state.detailsExpanded ? hideInclusionsLabel : viewInclusionsLabel}
            </SmallToggleButton>
          </ToggleWrapper>
        </InclusionHighlights>}
      </PackageOptionWrapper>
      {description && <StyledPaneDetailsSection isOpen={this.state.detailsExpanded} onToggle={this.toggleDetails}>
        <AllRoomDetails gap={12}>
          <BodyText variant="large" weight="bold">
            <FormattedMessage
              id="7QrbmVIGpsrr3nRL0eEh"
              defaultMessage="All Inclusions"
              
            />
          </BodyText>
          <MarkdownRender fontSize="medium" content={description} />
        </AllRoomDetails>
      </StyledPaneDetailsSection>}
    </Pane>
  }
}

const mapStateToProps = (state: App.State) => ({
  memberId: state.auth.account.memberId,
  authModal: state.ui.modal.open,
  search: state.router.location.search,
  currency: state.geo.currentCurrency,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
})

const mapDispatchToProps = {
  packageViewMoreDetailsClick,
  pushWithRegion,
  authModalOpen,
  authLogout,
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FlashTourPackageOption))
